/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode, ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { Menu } from "./Menu.js";
import { TalerLogo } from "./TalerLogo.js";
import { Banner } from "./Banner.js";
import { hasActiveDevOverrides } from "../stores/devSettings.js";
import { isExperimentalDeployment } from "../stores/webuiConfig.js";
import { useTranslation } from "../context/translation.js";

export interface LayoutProps {
  serverUrl?: string;
  accountName?: string;
  actionNeededCount?: number;
  kycWarningBanner?: string;
  onKycAction?: () => void;
  onSignOut?: () => void;
  showAdministration?: boolean;
  children: ComponentChildren;
}

export function Layout({
  serverUrl = "https://backend.demo.taler.net/",
  accountName = "default",
  actionNeededCount = 0,
  kycWarningBanner,
  onKycAction,
  onSignOut,
  showAdministration = false,
  children,
}: LayoutProps): VNode {
  const { t } = useTranslation();
  const isDevActive = hasActiveDevOverrides.value;
  const [isKycBannerDismissed, setIsKycBannerDismissed] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  return (
    <div class="flex flex-col md:flex-row min-h-screen bg-gray-50 text-gray-900">
      {/* Mobile Sticky Top Header */}
      <div class="md:hidden bg-gray-900 text-white px-4 py-3 flex items-center justify-between border-b border-gray-800 sticky top-0 z-40 shadow-md">
        <div class="flex items-center space-x-2.5">
          <TalerLogo class="h-6 w-auto" color="#ffffff" />
          <span class="text-xs font-bold text-gray-200">{t`Taler Merchant Portal`}</span>
        </div>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          class="p-1.5 rounded-md bg-gray-800 text-gray-300 hover:text-white focus:outline-none cursor-pointer"
          aria-label={t`Toggle navigation menu`}
        >
          <span class="text-base font-bold leading-none">{isMobileMenuOpen ? "✕" : "☰"}</span>
        </button>
      </div>

      <Menu
        serverUrl={serverUrl}
        accountName={accountName}
        actionNeededCount={actionNeededCount}
        isMobileOpen={isMobileMenuOpen}
        onCloseMobile={() => setIsMobileMenuOpen(false)}
        onSignOut={onSignOut}
        showAdministration={showAdministration}
      />
      <div class="flex-1 min-w-0 min-h-screen">
        <main class="max-w-6xl w-full min-w-0 p-4 sm:p-5 md:p-6">
        {/* Experimental Deployment Banner (configured via webui-config.json or #/dev overrides) */}
        {isExperimentalDeployment.value && (
          <div class="mb-4 p-3 bg-purple-900 text-purple-100 rounded-lg text-xs flex items-center justify-between shadow-xs">
            <div class="flex items-center space-x-2">
              <span class="font-bold text-2xs px-2 py-0.5 rounded bg-purple-700 text-purple-100 shadow-2xs">
                {t`⚠️ Experimental Deployment`}
              </span>
              <span class="font-semibold">
                {t`This service is running an experimental deployment. Features and APIs may be unstable or subject to change.`}
              </span>
            </div>
          </div>
        )}
        {/* Dev Overrides Unobtrusive Badge Header */}
        {isDevActive && (
          <div class="mb-4 flex justify-end">
            <a
              href="#/dev"
              class="inline-flex items-center space-x-1.5 px-3 py-1 text-2xs font-bold rounded-full bg-purple-100 text-purple-900 border border-purple-300 hover:bg-purple-200 transition-colors shadow-xs"
              title={t`Developer overrides are active. Click to manage settings in #dev`}
            >
              <span class="h-2 w-2 rounded-full bg-purple-600 animate-pulse"></span>
              <span>{t`🛠️ Dev Overrides Active`}</span>
            </a>
          </div>
        )}
        {kycWarningBanner && !isKycBannerDismissed && (
          <Banner
            type="warning"
            // Translators: Action button that opens the required identity
            // verification process.
            actionText={t`Complete identity check`}
            onAction={onKycAction}
            onDismiss={() => setIsKycBannerDismissed(true)}
          >
            {kycWarningBanner}
          </Banner>
        )}
        {children}
      </main>
    </div>
  </div>
  );
}
